/**
 * Repository Factory
 * Provides singleton instances of repositories
 * Switches between mock and real implementations based on config
 */

import { config } from '@/lib/config'

// Import mock implementations
import { MockAuthRepository } from './mock/MockAuthRepository'
import { MockInsightsRepository } from './mock/MockInsightsRepository'
import { MockProfileRepository } from './mock/MockProfileRepository'
import { MockAdminRepository } from './mock/MockAdminRepository'
import { MockAiRepository } from './mock/MockAiRepository'
import { MockTrackRepository } from './mock/MockTrackRepository'
import { MockDocumentRepository } from './mock/MockDocumentRepository'
import { MockUserManagementRepository } from './mock/MockUserManagementRepository'
import { MockPortfolioUpdateRepository } from './mock/MockPortfolioUpdateRepository'
import { MockSubscriptionRepository } from './mock/MockSubscriptionRepository'
import { MockActivePositionRepository } from './mock/MockActivePositionRepository'

// Import Supabase implementations
import {
  SupabaseAuthRepository,
  SupabaseInsightsRepository,
  SupabaseProfileRepository,
  SupabaseAdminRepository,
  SupabaseAiRepository,
  SupabaseTrackRepository,
  SupabaseDocumentRepository,
  SupabaseUserManagementRepository,
  SupabasePortfolioUpdateRepository,
  SupabaseSubscriptionRepository,
  SupabaseExemplarRepository,
  SupabaseActivePositionRepository,
} from './supabase'

// Import types
import type { IAuthRepository } from './interfaces/IAuthRepository'
import type { IInsightsRepository } from './interfaces/IInsightsRepository'
import type { IProfileRepository } from './interfaces/IProfileRepository'
import type { IAdminRepository } from './interfaces/IAdminRepository'
import type { IAiRepository } from './interfaces/IAiRepository'
import type { ITrackRepository } from './interfaces/ITrackRepository'
import type { IDocumentRepository } from './interfaces/IDocumentRepository'
import type { IUserManagementRepository } from './interfaces/IUserManagementRepository'
import type { IPortfolioUpdateRepository } from './interfaces/IPortfolioUpdateRepository'
import type { ISubscriptionRepository } from './interfaces/ISubscriptionRepository'
import type { IExemplarRepository } from './interfaces/IExemplarRepository'
import type { IActivePositionRepository } from './interfaces/IActivePositionRepository'

/**
 * Create repository instances based on config
 */
function createRepositories() {
  if (config.useBackend) {
    // Phase 2+: Use Supabase implementations
    return {
      auth: new SupabaseAuthRepository(),
      insights: new SupabaseInsightsRepository(),
      profile: new SupabaseProfileRepository(),
      admin: new SupabaseAdminRepository(),
      ai: new SupabaseAiRepository(),
      track: new SupabaseTrackRepository(),
      documents: new SupabaseDocumentRepository(),
      userManagement: new SupabaseUserManagementRepository(),
      portfolioUpdates: new SupabasePortfolioUpdateRepository(),
      subscription: new SupabaseSubscriptionRepository(),
      exemplar: new SupabaseExemplarRepository(),
      activePositions: new SupabaseActivePositionRepository(),
    }
  }

  // Mock mode: Use mock implementations
  return {
    auth: new MockAuthRepository(),
    insights: new MockInsightsRepository(),
    profile: new MockProfileRepository(),
    admin: new MockAdminRepository(),
    ai: new MockAiRepository(),
    track: new MockTrackRepository(),
    documents: new MockDocumentRepository(),
    userManagement: new MockUserManagementRepository(),
    portfolioUpdates: new MockPortfolioUpdateRepository(),
    subscription: new MockSubscriptionRepository(),
    exemplar: null as unknown as SupabaseExemplarRepository, // Sandbox is admin-only, no mock needed
    activePositions: new MockActivePositionRepository(),
  }
}

// Create singleton instances
const repositories = createRepositories()

// Export typed repositories
export const authRepo: IAuthRepository = repositories.auth
export const insightsRepo: IInsightsRepository = repositories.insights
export const profileRepo: IProfileRepository = repositories.profile
export const adminRepo: IAdminRepository = repositories.admin
export const aiRepo: IAiRepository = repositories.ai
export const trackRepo: ITrackRepository = repositories.track
export const documentRepo: IDocumentRepository = repositories.documents
export const userManagementRepo: IUserManagementRepository =
  repositories.userManagement
export const portfolioUpdateRepo: IPortfolioUpdateRepository =
  repositories.portfolioUpdates
export const subscriptionRepo: ISubscriptionRepository =
  repositories.subscription
export const exemplarRepo: IExemplarRepository = repositories.exemplar
export const activePositionRepo: IActivePositionRepository =
  repositories.activePositions

// Re-export types for convenience
export type {
  IAuthRepository,
  IInsightsRepository,
  IProfileRepository,
  IAdminRepository,
  IAiRepository,
  ITrackRepository,
  IDocumentRepository,
  IUserManagementRepository,
  IPortfolioUpdateRepository,
  ISubscriptionRepository,
}
export type { User } from './interfaces/IAuthRepository'
export type {
  Insight,
  CreateInsightData,
  UpdateInsightData,
} from './interfaces/IInsightsRepository'
export type {
  Profile,
  UpdateProfileData,
  OnboardingData,
} from './interfaces/IProfileRepository'
export type { TrackedInsight } from './interfaces/ITrackRepository'
export type {
  Document,
  DocumentType,
  UploadDocumentData,
} from './interfaces/IDocumentRepository'
export type { ManagedUser } from './interfaces/IUserManagementRepository'
export type { ConversationSummary } from './interfaces/IAiRepository'
export { CONVERSATION_MESSAGE_LIMIT } from './interfaces/IAiRepository'
export type {
  PortfolioUpdate,
  CreatePortfolioUpdateData,
} from './interfaces/IPortfolioUpdateRepository'
export type { SMSSubscription } from './interfaces/ISubscriptionRepository'
export type {
  Exemplar,
  CreateExemplarData,
  UpdateExemplarData,
  ExemplarCategory,
  ExemplarSourceType,
  ExemplarMatch,
  BulkImportResult,
} from './interfaces/IExemplarRepository'
export type {
  IActivePositionRepository,
  ActivePosition,
  CreatePositionData,
  UpdatePositionData,
  PositionSnapshot,
} from './interfaces/IActivePositionRepository'
